import { PlainModal } from '@design-system/components/modal/modal';
import { Button } from '@design-system/components/button/button';
import { Chip } from '@design-system/components/chip/chip';
import { Textarea } from '@design-system/components/textarea/textarea';
import { Typography } from '@design-system/components/typography/typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faXmark,
  faChevronLeft,
  faChevronRight,
  faWandMagicSparkles,
} from '@fortawesome/pro-regular-svg-icons';
import {
  aiTones,
  aiStyles,
  aiToneLabels,
  aiStyleLabels,
  AiTone,
  AiStyle,
} from '../../model/GenerateProfileText.model';
import {
  AiAssistVM,
  THINGS_TO_MENTION_MAX,
} from '../../view-model/AiAssist.viewModel';
import * as styles from './AiAssistModal.css';

type AiAssistModalProps = {
  vm: AiAssistVM;
  onAccept: () => void;
};

const SkeletonLoader = () => (
  <div className={styles.skeleton}>
    <div className={styles.skeletonLine({ width: 'full' })} />
    <div className={styles.skeletonLine({ width: 'full' })} />
    <div className={styles.skeletonLine({ width: 'medium' })} />
    <div className={styles.skeletonLine({ width: 'short' })} />
  </div>
);

export const AiAssistModal = ({ vm, onAccept }: AiAssistModalProps) => {
  if (!vm.isOpen) return null;

  const isGenerating = vm.generation.type === 'fetching';
  const hasResult = vm.generation.type === 'fetched';
  const hasError = vm.generation.type === 'fetch-error';
  const canGenerate = !isGenerating && vm.remainingGenerations !== 0;

  const handleAccept = () => {
    onAccept();
  };

  return (
    <PlainModal
      isOpen={vm.isOpen}
      onClose={vm.close}
      modalClassName={styles.modalOverride}
    >
      <div>
        <div className={styles.header}>
          <div className={styles.title}>
            <FontAwesomeIcon
              icon={faWandMagicSparkles}
              className={styles.titleIcon}
            />
            Improve with AI
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={vm.close}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} size="lg" />
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.section}>
            <Typography
              variant="body"
              size="small"
              className={styles.sectionLabel}
            >
              Tone
            </Typography>
            <div className={styles.chipRow}>
              {aiTones.map((tone) => (
                <Chip
                  key={tone}
                  label={aiToneLabels[tone]}
                  selected={vm.form.tone === tone}
                  onClick={() => vm.updateForm({ tone: tone as AiTone })}
                />
              ))}
            </div>
          </div>

          <div className={styles.section}>
            <Typography
              variant="body"
              size="small"
              className={styles.sectionLabel}
            >
              Length
            </Typography>
            <div className={styles.chipRow}>
              {aiStyles.map((s) => (
                <Chip
                  key={s}
                  label={aiStyleLabels[s]}
                  selected={vm.form.style === s}
                  onClick={() => vm.updateForm({ style: s as AiStyle })}
                />
              ))}
            </div>
          </div>

          {!hasResult && !isGenerating && (
            <div className={styles.fadeIn({ visible: true })}>
              <div className={styles.section}>
                <Typography
                  variant="body"
                  size="small"
                  className={styles.sectionLabel}
                >
                  Things to mention (optional)
                </Typography>
                <Textarea
                  rows={2}
                  maxLength={THINGS_TO_MENTION_MAX}
                  placeholder="E.g. 10 years of experience, certified trainer, specializing in HIIT"
                  value={vm.form.thingsToMention}
                  onChange={(e) =>
                    vm.updateForm({ thingsToMention: e.target.value })
                  }
                />
                <div className={styles.charCount}>
                  {vm.form.thingsToMention.length}/{THINGS_TO_MENTION_MAX}
                </div>
              </div>
            </div>
          )}

          {isGenerating && (
            <div className={styles.resultCard}>
              <SkeletonLoader />
            </div>
          )}

          {hasResult && vm.currentOption && (
            <div className={styles.fadeIn({ visible: true })}>
              <div className={styles.resultCard}>
                <div className={styles.resultText}>{vm.currentOption.text}</div>
                <div className={styles.resultMeta}>
                  <Typography
                    variant="body"
                    size="small"
                    className={styles.wordCount}
                  >
                    {vm.currentOption.wordCount} words
                  </Typography>
                  {vm.totalOptions > 1 && (
                    <div className={styles.resultNav}>
                      <button
                        type="button"
                        className={styles.navButton}
                        onClick={vm.prevOption}
                      >
                        <FontAwesomeIcon icon={faChevronLeft} />
                      </button>
                      <span className={styles.navLabel}>
                        {vm.selectedIndex + 1} of {vm.totalOptions}
                      </span>
                      <button
                        type="button"
                        className={styles.navButton}
                        onClick={vm.nextOption}
                      >
                        <FontAwesomeIcon icon={faChevronRight} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {hasError && (
            <div className={styles.errorMessage}>
              {vm.generation.type === 'fetch-error' && vm.generation.value}
            </div>
          )}

          {vm.remainingGenerations !== null && (
            <div className={styles.generationsLeft}>
              {vm.remainingGenerations} generation
              {vm.remainingGenerations !== 1 ? 's' : ''} remaining today
            </div>
          )}
        </div>

        <div className={styles.actions}>
          {hasResult ? (
            <>
              <Button
                variant="outlined"
                color="neutral"
                size="medium"
                onClick={vm.close}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                color="neutral"
                size="medium"
                onClick={vm.generate}
                disabled={!canGenerate}
              >
                Try again
              </Button>
              <Button
                variant="filled"
                color="neutral"
                size="medium"
                onClick={handleAccept}
              >
                Accept
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                color="neutral"
                size="medium"
                onClick={vm.close}
              >
                Cancel
              </Button>
              <Button
                variant="filled"
                color="neutral"
                size="medium"
                onClick={vm.generate}
                loading={isGenerating}
                disabled={!canGenerate}
                leftIcon={faWandMagicSparkles}
              >
                Generate
              </Button>
            </>
          )}
        </div>
      </div>
    </PlainModal>
  );
};
